"use client";

import { useTranslations, useLocale } from "next-intl";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BranchCombobox, {
  type BranchComboboxMethodOption,
} from "./BranchCombobox";
import { getDeliveryLabel } from "@/lib/utils/delivery-label";
import type { Branch } from "@/types";

/**
 * 이 수 이하이면 기존 Radix Select 를 그대로 쓴다.
 * 초과하면 검색 + 시/도 그룹 + "가까운 지점" 을 갖춘 콤보박스로 전환한다
 * (한 상품이 최대 122개 지점 / 60개 도시에 걸린다).
 */
const COMBOBOX_THRESHOLD = 8;

interface BranchSelectorProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  /** Filter branches by location_type: "pickup" filters pickup|both, "return" filters return|both */
  locationType: "pickup" | "return";
  branches: Branch[];
  hasPickupMethod: boolean;
  hasShippingMethod: boolean;
  hasDeliveryMethod: boolean;
  /** Partner country → "한국 내 배송" style delivery label. */
  partnerCountryCode?: string | null;
}

export default function BranchSelector({
  value,
  onValueChange,
  placeholder,
  locationType,
  branches,
  hasPickupMethod,
  hasShippingMethod,
  hasDeliveryMethod,
  partnerCountryCode,
}: BranchSelectorProps) {
  const tMethods = useTranslations("product.usageMethods");
  const t = useTranslations("product.booking");
  const locale = useLocale();

  const getMethodName = (methodType: "pickup" | "delivery" | "shipping") => {
    if (methodType === "delivery") {
      return getDeliveryLabel(partnerCountryCode, locale, tMethods, {
        templateKey: "deliveryTemplate",
        fallbackKey: "delivery",
      });
    }
    return tMethods(methodType);
  };

  const filteredBranches = branches.filter(
    (branch) =>
      (branch.location_type === locationType ||
        branch.location_type === "both") &&
      branch.branch_type === "physical",
  );

  const keyPrefix = locationType === "return" ? "-return" : "";

  const branchesLabel =
    locationType === "return" ? t("returnAtLocation") : getMethodName("pickup");

  // 지점이 많으면 검색형 콤보박스. value/onValueChange/placeholder 의미는 동일하다
  // ("shipping" / "delivery" 특수 값 포함).
  if (hasPickupMethod && filteredBranches.length > COMBOBOX_THRESHOLD) {
    const methodOptions: BranchComboboxMethodOption[] = [];
    if (hasShippingMethod) {
      methodOptions.push({
        value: "shipping",
        label: getMethodName("shipping"),
      });
    }
    if (hasDeliveryMethod) {
      methodOptions.push({
        value: "delivery",
        label: getMethodName("delivery"),
      });
    }

    return (
      <BranchCombobox
        value={value}
        onValueChange={onValueChange}
        placeholder={placeholder}
        branches={filteredBranches}
        branchesLabel={branchesLabel}
        methodOptions={methodOptions}
      />
    );
  }

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="w-full px-3">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {hasPickupMethod && filteredBranches.length > 0 && (
          <SelectGroup>
            <SelectLabel>{branchesLabel}</SelectLabel>
            {filteredBranches.map((branch) => (
              <SelectItem key={branch.id} value={branch.id}>
                {branch.name}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {hasShippingMethod && (
          <>
            {hasPickupMethod && filteredBranches.length > 0 && <SelectSeparator />}
            {/* 숙소 수령: 그룹 라벨 없이 항목만 (라벨=항목명 중복) */}
            <SelectGroup>
              <SelectItem key={`shipping${keyPrefix}`} value="shipping">
                {getMethodName("shipping")}
              </SelectItem>
            </SelectGroup>
          </>
        )}
        {hasDeliveryMethod && (
          <>
            {(hasPickupMethod || hasShippingMethod) && <SelectSeparator />}
            {/* 국내 배송: 그룹 라벨 없이 항목만 (라벨=항목명 중복) */}
            <SelectGroup>
              <SelectItem key={`delivery${keyPrefix}`} value="delivery">
                {getMethodName("delivery")}
              </SelectItem>
            </SelectGroup>
          </>
        )}
      </SelectContent>
    </Select>
  );
}
